import {
  weeksBeforeContingentPackageExpireAlertShouldBeShown,
  weeksBeforeVoucherNAPackageExpireAlertShouldBeShown,
} from '@common';
import { createSelector, DefaultProjectorFn, MemoizedSelector } from '@ngrx/store';

import {
  BOOKING_TRIAL_STATUS,
  BookingDashboardDevice,
  BookingDataForPriceCalculation,
  BookingPagination,
  BookingPlansLoadingState,
  BookingSubscription,
  BookingSubscriptionsPlanByNames,
  CANCELING_STATUS,
  getDevicePlatformBasingOnRoles,
  hasMainDeviceRole,
  IAMRoles,
  Platform,
  VoucherValidationResponse,
} from '@store';

import {
  ContingentSubscription,
  DeviceStatusType,
  ErrorType,
  InstallationItemGatewayDevice,
  Subscription,
  SubscriptionPlan,
  SubscriptionsDetails,
  SubscriptionStatus,
  TrialSubscriptionsList,
  VoucherValidationErrorResponse,
} from '@vi-iot/client';
import { camelCase } from 'lodash-es';
import moment from 'moment';

import { AppState } from '../app.state';
import { APPLICATION_ROLES, AuthSelectors, AuthState } from '../auth';
import { InstallationPackageStatus, InstallationsState } from '../installations';
import { RemoteConfigState } from '../remote-config';

import { BookingState } from './booking.state';

export namespace BookingSelectors {
  export const booking = (state: AppState): BookingState => state.booking;
  export const installations = (state: AppState): InstallationsState => state.installations;
  export const auth = (state: AppState): AuthState => state.auth;
  export const remoteConfig = (state: AppState): RemoteConfigState => state.remoteConfig;

  export function getSelectedPlan(): MemoizedSelector<
    AppState,
    SubscriptionPlan,
    DefaultProjectorFn<SubscriptionPlan>
  > {
    return createSelector(booking, (state: BookingState) => state.plan);
  }

  export const selectSelectedDevices = createSelector(
    booking,
    (state: BookingState): BookingDashboardDevice[] => state.bookingDevicesSelected
  );

  export function getDevicesCount(): MemoizedSelector<
    AppState,
    number,
    DefaultProjectorFn<number>
  > {
    return createSelector(booking, (state: BookingState) => state.allDevicesCount);
  }

  export function isSelectedInstallation(
    deviceSerial: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      booking,
      (state: BookingState) =>
        !!state.bookingDevicesSelected.find(({ vin }) => deviceSerial === vin)
    );
  }

  export function getSelectedInstallationsSerials(): MemoizedSelector<
    AppState,
    string[],
    DefaultProjectorFn<string[]>
  > {
    return createSelector(
      selectSelectedDevices,
      (selectedInstallations: BookingDashboardDevice[]) =>
        selectedInstallations.map((installation: BookingDashboardDevice) => installation.vin)
    );
  }

  export function getSelectedInstallationsCount(): MemoizedSelector<
    AppState,
    number,
    DefaultProjectorFn<number>
  > {
    return createSelector(booking, (state: BookingState) => state.bookingDevicesSelected.length);
  }

  export function getBookingInstallation(): MemoizedSelector<
    AppState,
    BookingDashboardDevice[],
    DefaultProjectorFn<BookingDashboardDevice[] | null>
  > {
    return createSelector(booking, (state: BookingState) => state.bookingDevices.value);
  }

  export const selectBookingDevicesWithFilter = createSelector(
    booking,
    (state: BookingState): BookingDashboardDevice[] => {
      const searchInput = state.searchInput?.toLowerCase();

      if (searchInput) {
        return state.bookingDevices.value.filter((device) => {
          if (
            device.vin?.toLowerCase().includes(searchInput) ||
            device.marketingName?.toLowerCase().includes(searchInput) ||
            device.installationId.toString()?.toLowerCase().includes(searchInput) ||
            device.ownerName?.toLowerCase().includes(searchInput) ||
            device.location?.toLowerCase().includes(searchInput)
          ) {
            return true;
          }
        });
      }

      return state.bookingDevices.value;
    }
  );

  export const selectBookingDevicesWithFilterAndPagination = createSelector(
    booking,
    selectBookingDevicesWithFilter,
    (state: BookingState, bookingDevices: BookingDashboardDevice[]) => {
      // 1st page -> 0;24
      // 2nd page -> 25;49
      const paginationStart = state.pagination.page * state.pagination.pageSize;
      const paginationEnd = (state.pagination.page + 1) * state.pagination.pageSize;

      return bookingDevices.slice(paginationStart, paginationEnd);
    }
  );

  export function allInstallationsSelected(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(booking, (state: BookingState) => {
      return state.bookingDevices.value?.length === state.bookingDevicesSelected.length;
    });
  }

  export function selectSubscriptionsPlans(): MemoizedSelector<
    AppState,
    BookingSubscriptionsPlanByNames,
    DefaultProjectorFn<BookingSubscriptionsPlanByNames>
  > {
    return createSelector(booking, (state: BookingState) => {
      // Return array of objects based on plans' details. Set key basing on plan's name
      const subscriptionsPlansToObject: BookingSubscriptionsPlanByNames = state.subscriptionsPlans.value.reduce(
        (accumulator, plan) => {
          const planName = camelCase(plan.name);
          const planToAdd = {
            ...accumulator,
            [planName]: plan,
          };

          return planToAdd;
        },
        {} as BookingSubscriptionsPlanByNames
      );

      return subscriptionsPlansToObject;
    });
  }

  export function selectSubscriptions(): MemoizedSelector<
    AppState,
    BookingSubscription[],
    DefaultProjectorFn<BookingSubscription[]>
  > {
    return createSelector(booking, (state: BookingState) => state.subscriptions.value);
  }

  export function selectLoadedState(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(booking, (state: BookingState) => state.loaded);
  }

  export function selectIsLoadingState(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(booking, (state: BookingState) => state.loading);
  }

  export function selectBookingLoadedState(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      booking,
      (state: BookingState) => state.loaded && state.subscriptions.loaded
    );
  }

  export function getAmountOfAvailableVouchers(
    invoiceNumber: string
  ): MemoizedSelector<AppState, number, DefaultProjectorFn<number>> {
    return createSelector(booking, (state: BookingState) => {
      const selectedInvoice = state.contingentSubscriptions.find(
        (contingentSubscription) => contingentSubscription.invoice === invoiceNumber
      );

      return selectedInvoice.free;
    });
  }

  export function selectSubscriptionsDetailsLoadedState(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(booking, (state: BookingState) => state.subscriptionsDetails.loaded);
  }

  export function selectBookingPlansLoadedState(): MemoizedSelector<
    AppState,
    BookingPlansLoadingState,
    DefaultProjectorFn<BookingPlansLoadingState>
  > {
    return createSelector(booking, (state: BookingState) => {
      const isLoaded =
        state.subscriptionsPlans.loaded &&
        state.trialSubscriptions.loaded &&
        state.subscriptions.loaded;

      const isError =
        state.subscriptionsPlans.error ||
        state.trialSubscriptions.error ||
        state.subscriptions.error;

      const isLoading =
        state.subscriptionsPlans.loading ||
        state.trialSubscriptions.loading ||
        state.subscriptions.loading;

      if (isLoaded) {
        return BookingPlansLoadingState.Loaded;
      } else if (isError) {
        return BookingPlansLoadingState.Error;
      } else if (isLoading) {
        return BookingPlansLoadingState.Loading;
      } else {
        return BookingPlansLoadingState.Other;
      }
    });
  }

  export function getVoucherValidationState(): MemoizedSelector<
    AppState,
    VoucherValidationResponse,
    DefaultProjectorFn<VoucherValidationResponse>
  > {
    return createSelector(booking, (state: BookingState) => {
      if (state.voucherValidation.valid) {
        return VoucherValidationResponse.Valid;
      } else if (
        state.voucherValidation.error?.extendedPayload.errorType ===
          VoucherValidationErrorResponse.VOUCHER_ALREADY_USED ||
        state.voucherValidation.reasons?.includes(
          VoucherValidationErrorResponse.VOUCHER_ALREADY_USED
        )
      ) {
        return VoucherValidationResponse.AlreadyUsed;
      } else if (
        state.voucherValidation.error?.extendedPayload?.errorMessage ===
          VoucherValidationErrorResponse.WRONG_VOUCHER_CODE ||
        state.voucherValidation.reasons?.includes(
          VoucherValidationErrorResponse.INVALID_VOUCHER_TYPE
        )
      ) {
        return VoucherValidationResponse.Invalid;
      } else {
        return VoucherValidationResponse.Required;
      }
    });
  }

  export function selectCommercialBookingPlansLoadedState(): MemoizedSelector<
    AppState,
    BookingPlansLoadingState,
    DefaultProjectorFn<BookingPlansLoadingState>
  > {
    return createSelector(booking, (state: BookingState) => {
      const isLoaded = state.subscriptionsPlans.loaded && state.subscriptions.loaded;

      const isError = state.subscriptionsPlans.error || state.subscriptions.error;

      const isLoading = state.subscriptionsPlans.loading || state.subscriptions.loading;

      if (isLoaded) {
        return BookingPlansLoadingState.Loaded;
      } else if (isError) {
        return BookingPlansLoadingState.Error;
      } else if (isLoading) {
        return BookingPlansLoadingState.Loading;
      } else {
        return BookingPlansLoadingState.Other;
      }
    });
  }

  export function selectTrialSubscriptions(): MemoizedSelector<
    AppState,
    TrialSubscriptionsList,
    DefaultProjectorFn<TrialSubscriptionsList>
  > {
    return createSelector(booking, (state: BookingState) => {
      return state.trialSubscriptions.value;
    });
  }

  export function selectContingentSubscriptions(): MemoizedSelector<
    AppState,
    ContingentSubscription[],
    DefaultProjectorFn<ContingentSubscription[]>
  > {
    return createSelector(booking, (state: BookingState) => state.contingentSubscriptions);
  }

  export function hasAccessToBooking(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      remoteConfig,
      auth,
      AuthSelectors.isCompanyFromUsaOrCanada(),
      (
        remoteConfigState: RemoteConfigState,
        authState: AuthState,
        isCompanyFromUsaOrCanada: boolean
      ) => {
        const canManageSubscriptions = !!authState.me?.applicationRoles?.find(
          (role) => role === APPLICATION_ROLES.VITOGUIDE_CAN_MANAGE_SUBSCRIPTIONS
        );

        const isEndUser = !!authState?.me?.properties?.find(
          (property) => property.value === IAMRoles.endCustomer
        );

        const hasAccess =
          canManageSubscriptions &&
          !isEndUser &&
          (remoteConfigState.remoteConfig.areProPlusFeaturesForCompanyIdAvailable ||
            remoteConfigState.remoteConfig.areProPlusFeaturesForVkOrgAvailable);

        // https://jira.viessmann-climatesolutions.com/browse/VIG-12702
        if (isCompanyFromUsaOrCanada) {
          return hasAccess && remoteConfigState.remoteConfig.isCanadaAndUSABookingAvailable;
        } else {
          return hasAccess;
        }
      }
    );
  }

  export function selectSubscriptionsDetails(): MemoizedSelector<
    AppState,
    SubscriptionsDetails,
    DefaultProjectorFn<SubscriptionsDetails>
  > {
    return createSelector(booking, (state: BookingState) => state.subscriptionsDetails.value);
  }

  export function selectError(): MemoizedSelector<AppState, any, DefaultProjectorFn<any>> {
    return createSelector(booking, (state: BookingState) => state.error);
  }

  export function selectPurchaseTrialStatus(): MemoizedSelector<
    AppState,
    BOOKING_TRIAL_STATUS,
    DefaultProjectorFn<BOOKING_TRIAL_STATUS>
  > {
    return createSelector(booking, (state: BookingState) => state.bookingTrialStatus);
  }

  export function selectBuySubscriptionError(): MemoizedSelector<
    AppState,
    any,
    DefaultProjectorFn<any>
  > {
    return createSelector(booking, (state: BookingState) => state.buySubscriptionError);
  }

  export function isSimulatingSubscription(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(booking, (state: BookingState) => state.simulateSubscription.loading);
  }

  export function getSubscriptionSimulatedError(): MemoizedSelector<
    AppState,
    any,
    DefaultProjectorFn<any>
  > {
    return createSelector(booking, (state: BookingState) => state.simulateSubscription.error);
  }

  export function getSubscriptionSimulatedSuccessfully(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      booking,
      (state: BookingState) =>
        !state.simulateSubscription.error && state.simulateSubscription.loaded
    );
  }

  export function isOnlyOnePlusSubscriptionSelectedToBeUpdatedToProPlan(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      selectSelectedDevices,
      getActiveSubscriptionsAsPackageStatus,
      (
        selectedInstallations: BookingDashboardDevice[],
        activeSubscriptionsPackageStatus: InstallationPackageStatus[]
      ): boolean => {
        let isOnlyOneUpdate: boolean;
        if (selectedInstallations?.length === 1) {
          isOnlyOneUpdate = activeSubscriptionsPackageStatus.some(
            (entry) => selectedInstallations[0].vin === entry.boilerSerial
          );
        }

        return !!isOnlyOneUpdate;
      }
    );
  }

  export const getSelectedInstallationsPlatformsWithPriceData = createSelector(
    selectSubscriptionsPlans(),
    selectSelectedDevices,
    (
      plans: BookingSubscriptionsPlanByNames,
      selectedInstallations: BookingDashboardDevice[]
    ): BookingDataForPriceCalculation => ({
      plans,
      installations: selectedInstallations.reduce(
        (acc, installation) => {
          if (installation.platform === Platform.E3) {
            acc.e3Devices.push(installation);
          }
          if (installation.platform === Platform.Legacy) {
            acc.legacyDevices.push(installation);
          }

          return acc;
        },
        {
          e3Devices: [],
          legacyDevices: [],
        }
      ),
    })
  );

  // Flat rate allows only one active pro subscription
  export const activeOrCanceledFlatRateSubscription = createSelector(
    selectSubscriptions(),
    (subscriptions: BookingSubscription[]): BookingSubscription =>
      subscriptions.find(
        (subscription) =>
          // Pro package

          ((subscription.status?.toLowerCase() === SubscriptionStatus.active ||
            subscription.status?.toLowerCase() === SubscriptionStatus.canceled) &&
            subscription.boilerId === '') ||
          // Trial package
          (subscription.status?.toLowerCase() === SubscriptionStatus.active &&
            subscription.boilerId &&
            subscription.trial)
      )
  );

  export const selectActiveOrRenewVoucherSubscription = createSelector(
    selectSubscriptions(),
    (subscriptions: BookingSubscription[]): BookingSubscription[] => {
      const futureSubscriptions = [];
      const activeSubscriptions = [];

      subscriptions.map((sub) => {
        if (sub.status?.toLowerCase() === SubscriptionStatus.future) {
          return futureSubscriptions.push(sub);
        }
      });
      subscriptions.map((sub) => {
        if (sub.status?.toLowerCase() === SubscriptionStatus.active && !sub.trial) {
          return activeSubscriptions.push(sub);
        }
      });

      //when renew sub we've got 2 sub for one installation with diff statuses
      const activeOrRenewSub = activeSubscriptions.map((sub) => {
        const renewSub = futureSubscriptions.find((sub2) => sub2.boilerId === sub.boilerId);

        return renewSub ? { ...sub, ...renewSub } : sub;
      });

      return activeOrRenewSub;
    }
  );

  // Commercial allows only one active pro or basic subscription
  export const activeOrCanceledCommercialSubscription = createSelector(
    selectSubscriptions(),
    (subscriptions: BookingSubscription[]): BookingSubscription =>
      subscriptions.find(
        (subscription) =>
          // Pro or basic package
          (subscription.status?.toLowerCase() === SubscriptionStatus.active ||
            subscription.status?.toLowerCase() === SubscriptionStatus.canceled) &&
          subscription.boilerId === '' &&
          !subscription.trial &&
          (subscription.plan === SubscriptionPlan.Pro ||
            subscription.plan === SubscriptionPlan.Basic)
      )
  );

  export const activeOrCanceledBasicCommercialSubscription = createSelector(
    selectSubscriptions(),
    (subscriptions: BookingSubscription[]): BookingSubscription =>
      subscriptions.find(
        (subscription) =>
          // Basic package
          (subscription.status?.toLowerCase() === SubscriptionStatus.active ||
            subscription.status?.toLowerCase() === SubscriptionStatus.canceled) &&
          subscription.boilerId === '' &&
          !subscription.trial &&
          subscription.plan === SubscriptionPlan.Basic
      )
  );

  // Flat rate allows only one active pro subscription
  export const getActiveProSubscriptionForFlatRate = createSelector(
    activeOrCanceledFlatRateSubscription,
    (subscription): BookingSubscription | null => (!subscription?.trial ? subscription : null)
  );

  // Flat rate allows only one active pro subscription
  export const getActiveTrialSubscriptionForFlatRate = createSelector(
    activeOrCanceledFlatRateSubscription,
    (subscription): BookingSubscription | null => (subscription?.trial ? subscription : null)
  );

  export const expiredFlatRateSubscriptions = createSelector(
    selectSubscriptions(),
    (subscriptions: BookingSubscription[]): BookingSubscription[] =>
      subscriptions.filter(
        (subscription) => subscription.status?.toLowerCase() === SubscriptionStatus.expired
      )
  );

  export const expiredCommercialSubscriptions = createSelector(
    selectSubscriptions(),
    (subscriptions: BookingSubscription[]): BookingSubscription[] =>
      subscriptions.filter(
        (subscription) =>
          subscription.status?.toLowerCase() === SubscriptionStatus.expired &&
          subscription.boilerId === '' &&
          !subscription.trial &&
          (subscription.plan === SubscriptionPlan.Pro ||
            subscription.plan === SubscriptionPlan.Basic)
      )
  );

  export const selectExpiredVoucherSubscriptions = createSelector(
    selectSubscriptions(),
    (subscriptions: BookingSubscription[]): BookingSubscription[] =>
      subscriptions.filter(
        (subscription) => subscription.status?.toLowerCase() === SubscriptionStatus.expired
      )
  );

  export const trialActiveSubscriptions = createSelector(
    selectSubscriptions(),
    (subscriptions: BookingSubscription[]): BookingSubscription[] =>
      subscriptions.filter(
        (subscription) =>
          subscription.status?.toLowerCase() === SubscriptionStatus.active && subscription.trial
      )
  );

  export function activeSubscriptionsByPlanNames(
    planNames: SubscriptionPlan[]
  ): MemoizedSelector<AppState, BookingSubscription[], DefaultProjectorFn<BookingSubscription[]>> {
    return createSelector(
      selectSubscriptions(),
      (subscriptions: BookingSubscription[]): BookingSubscription[] =>
        subscriptions.filter(
          (subscription) =>
            planNames.some(
              (planName) => subscription.plan.toLowerCase() === planName.toLowerCase()
            ) && subscription.status?.toLowerCase() === SubscriptionStatus.active
        )
    );
  }

  const twoWeeksInMilliseconds = 1209600000;
  export const trialActiveSubscriptionsEndingSoon = createSelector(
    selectSubscriptions(),
    (subscriptions: BookingSubscription[]): BookingSubscription[] =>
      subscriptions.filter(
        (subscription) =>
          subscription.status?.toLowerCase() === SubscriptionStatus.active &&
          subscription.trial &&
          new Date(+new Date() + twoWeeksInMilliseconds) > new Date(subscription.expirationDate)
      )
  );

  export const automaticExtensionSubscriptionsEndingSoon = createSelector(
    selectSubscriptions(),
    (subscriptions: BookingSubscription[]): BookingSubscription[] =>
      subscriptions.filter(
        (subscription) =>
          subscription.status?.toLowerCase() === SubscriptionStatus.active &&
          subscription.plan === SubscriptionPlan.Pro &&
          subscription.originalBundleInvoice &&
          moment(subscription.expirationDate).isBefore(
            moment().add(weeksBeforeContingentPackageExpireAlertShouldBeShown, 'weeks')
          ) &&
          !subscription.trial
      )
  );

  export const trialActiveSubscriptionsOnline = createSelector(
    trialActiveSubscriptions,
    installations,
    (
      trialActiveSubscriptionsArray: BookingSubscription[],
      installationsState: InstallationsState
    ): BookingSubscription[] =>
      trialActiveSubscriptionsArray.filter((subscription) => {
        const mainDevicePlatform = getPlatformOfSubscriptionInstallation(
          installationsState,
          subscription
        );

        return (
          getDeviceNetworkStatus(installationsState, subscription.boilerId) ===
            DeviceStatusType.Online && mainDevicePlatform !== Platform.None
        );
      })
  );

  export const trialActiveSubscriptionsOfflineForBooking = createSelector(
    trialActiveSubscriptions,
    installations,
    (
      trialActiveSubscriptionsArray: BookingSubscription[],
      installationsState: InstallationsState
    ): BookingSubscription[] => {
      return trialActiveSubscriptionsArray.filter((subscription) => {
        const mainDevicePlatform = getPlatformOfSubscriptionInstallation(
          installationsState,
          subscription
        );

        return (
          getDeviceNetworkStatus(installationsState, subscription.boilerId) ===
            DeviceStatusType.Offline || mainDevicePlatform === Platform.None
        );
      });
    }
  );

  export function getDeviceNetworkStatus(
    installationsState: InstallationsState,
    deviceId: string
  ): DeviceStatusType {
    let resultStatus = null;
    installationsState.installations.forEach((installation) =>
      installation.gateways.forEach((gateway) => {
        const matchedDevice = gateway.devices.find((device) => device.serial === deviceId);
        if (matchedDevice) {
          resultStatus = matchedDevice.status;
        }
      })
    );

    return resultStatus;
  }

  export const getActiveSubscriptionsAsPackageStatus = createSelector(
    selectSubscriptions(),
    (subscriptions): InstallationPackageStatus[] => {
      const activeSubscriptionsAsPackageStatus: InstallationPackageStatus[] = [];

      subscriptions.forEach((entry: Subscription) => {
        if (entry.status?.toLowerCase() !== SubscriptionStatus.expired) {
          const packageEntry: InstallationPackageStatus = {
            plan: entry.plan,
            boilerSerial: entry.boilerId,
            isTrial: entry.trial,
          };
          activeSubscriptionsAsPackageStatus.push(packageEntry);
        }
      });

      return activeSubscriptionsAsPackageStatus;
    }
  );

  export const subscriptionsLoaded = createSelector(
    booking,
    (state): boolean => state.subscriptions.loaded
  );

  export const getCancelSubscriptionStatus = createSelector(
    booking,
    (state): CANCELING_STATUS => state.cancelSubscriptionStatus
  );

  export function getCurrencyOfProPackage(): MemoizedSelector<
    AppState,
    string,
    DefaultProjectorFn<string>
  > {
    return createSelector(booking, (state: BookingState) => {
      const proPlan = state.subscriptionsPlans.value.find(
        (plan) => plan.name === SubscriptionPlan.Pro
      );

      return proPlan?.price?.currency;
    });
  }

  export const showPlantOverviewLink = createSelector(booking, (state): boolean => {
    const error = state.buySubscriptionError;

    return (
      error?.extendedPayload?.errorType === ErrorType.EQUIPMENT_ID_NOT_FOUND_FOR_BOILER_ID ||
      error?.errorType === ErrorType.EQUIPMENT_ID_NOT_FOUND_FOR_BOILER_ID
    );
  });

  export const selectBookableDevicesOfGivenInstallation = (
    installationId: number
  ): MemoizedSelector<
    AppState,
    BookingDashboardDevice[],
    DefaultProjectorFn<BookingDashboardDevice[]>
  > => {
    return createSelector(getBookingInstallation(), (devices: BookingDashboardDevice[]) => {
      return devices?.filter((device) => device.installationId === installationId) ?? [];
    });
  };

  export const selectBoilerSerialsOfBookableDevicesOfGivenInstallation = (
    installationId: number
  ): MemoizedSelector<AppState, string[], DefaultProjectorFn<string[]>> => {
    return createSelector(
      selectBookableDevicesOfGivenInstallation(installationId),
      (devices: BookingDashboardDevice[]) => {
        return devices.map((device) => device.vin);
      }
    );
  };

  function getPlatformOfSubscriptionInstallation(
    installationsState: InstallationsState,
    subscription: BookingSubscription
  ): Platform {
    const installation = installationsState.installations.find(
      (entry) => entry.id === subscription.installationId
    );
    if (installation) {
      let mainDevice: InstallationItemGatewayDevice;
      installation.gateways.forEach((gateway) => {
        mainDevice = gateway.devices.find((device) => hasMainDeviceRole(device));
      });
      if (mainDevice) {
        const mainDevicePlatform = getDevicePlatformBasingOnRoles(mainDevice.roles);

        return mainDevicePlatform;
      }
    }

    return Platform.None;
  }

  export function selectIfActiveVoucherSubCanBeRenew(
    boilerSerial: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      selectSubscriptions(),
      (subscriptions: BookingSubscription[]): boolean => {
        const activeAndFutureSubscription = subscriptions.filter(
          (sub) =>
            sub.boilerId === boilerSerial &&
            !sub.trial &&
            sub.voucher &&
            (sub.status?.toLowerCase() === SubscriptionStatus.future ||
              sub.status?.toLowerCase() === SubscriptionStatus.active)
        );

        if (activeAndFutureSubscription.length !== 1) {
          return false;
        } else {
          const expDate = activeAndFutureSubscription.find(
            (sub) => sub?.status.toLowerCase() === SubscriptionStatus.active
          )?.expirationDate;

          return moment(expDate).isBefore(
            moment().add(weeksBeforeVoucherNAPackageExpireAlertShouldBeShown, 'weeks')
          );
        }
      }
    );
  }

  export const selectSearchInputValue = createSelector(
    booking,
    (state: BookingState): string => state.searchInput
  );

  export const selectPagination = createSelector(
    booking,
    (state: BookingState): BookingPagination => state.pagination
  );

  export const selectIsBookingDevicesInInitState = createSelector(
    booking,
    (state: BookingState): boolean => !state.bookingDevices.value && !state.bookingDevices.loading
  );

  export const selectIsBookingDevicesLoading = createSelector(
    booking,
    (state: BookingState): boolean => state.bookingDevices.loading
  );
}
